import {
  ArrowLeftIcon,
  DocumentTextIcon,
  PencilIcon,
  ReceiptRefundIcon,
  TruckIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import EditDealModal, { EditableDeal } from '../../components/deals/EditDealModal';
import GPSDeviceManager from '../../components/deals/GPSDeviceManager';
import LinkVehicleCard from '../../components/deals/LinkVehicleCard';
import { apiService, extractApiErrorMessage } from '../../services/api';
import { downloadDealDocuments } from '../../services/dealDocuments';
import {
  formatDealType,
  isBhphDeal,
  isFinancedDeal,
  normalizeDealType,
} from '../../utils/dealType';

interface Deal {
  id: string;
  deal_number: string;
  deal_type: string;
  deal_status: string;
  customer_id?: string;
  vehicle_id?: string;
  salesperson_id?: string;
  customer_first_name?: string;
  customer_last_name?: string;
  customer_phone?: string;
  salesperson_name?: string;
  year?: number;
  make?: string;
  model?: string;
  trim?: string;
  stock_number?: string;
  vin?: string;
  // NOTE: field names match the backend DealRead/DealUpdate schema. The legacy
  // aliases apr / down_payment / financed_amount never matched the API and
  // always rendered N/A; corrected to interest_rate / down_payment_amount /
  // amount_financed. selling_price has no backend field yet (phantom — price
  // modeling is a separate slice); left here only so the display card compiles.
  purchase_price?: number;
  total_vehicle_cost?: number;
  front_gross?: number;
  backend_gross?: number;
  total_gross?: number;
  selling_price?: number;
  down_payment_amount?: number;
  amount_financed?: number;
  monthly_payment?: number;
  term_months?: number;
  interest_rate?: number;
  sales_tax?: number;
  total_fees?: number;
  lender_name?: string;
  credit_score?: number;
  credit_tier?: string;
  deal_score?: string;
  created_at: string;
  updated_at?: string;
  notes?: string;
}

const DealDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [deal, setDeal] = useState<Deal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [closing, setClosing] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);
  const [scheduleModal, setScheduleModal] = useState<{ open: boolean; rows: any[] }>({
    open: false,
    rows: [],
  });
  const [noteModal, setNoteModal] = useState({ open: false, text: '', submitting: false });
  const [markingSold, setMarkingSold] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const fetchDeal = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.get<Deal>(`/deals/${id}`);
      setDeal(response);
    } catch (error) {
      console.error('Error fetching deal:', error);
      setError('Failed to load deal details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDeal();
  }, [fetchDeal]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getDealTypeBadge = (type: string) => {
    const colors = {
      CASH: 'bg-green-100 text-green-800',
      OUTSIDE_FINANCE: 'bg-blue-100 text-blue-800',
      BHPH: 'bg-purple-100 text-purple-800',
    };
    return colors[normalizeDealType(type) as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const closeDealToBHPH = async () => {
    if (!id || !deal) return;
    setClosing(true);
    setCloseError(null);
    try {
      const result = await apiService.post<{
        bhph_account_id: string;
        bhph_account_number: string;
      }>(`/deals/${id}/close-to-bhph`, {});
      navigate(`/bhph/account/${result.bhph_account_id}`);
    } catch (err: unknown) {
      // Surface the real backend reason (e.g. "Deal must be finalized before a
      // BHPH account can be created") instead of the generic axios message.
      setCloseError(extractApiErrorMessage(err, 'Failed to close deal'));
    } finally {
      setClosing(false);
    }
  };

  const viewPaymentSchedule = async () => {
    if (!deal?.amount_financed || !deal?.interest_rate || !deal?.term_months) {
      toast.error('No financing data available for this deal');
      return;
    }
    try {
      const rows = await apiService.post<any[]>('/deals/payment-schedule', {
        principal: deal.amount_financed,
        apr: deal.interest_rate,
        term_months: deal.term_months,
      });
      setScheduleModal({ open: true, rows });
    } catch {
      toast.error('Failed to load payment schedule');
    }
  };

  const recordPayment = async () => {
    if (!deal?.deal_number) return;
    try {
      const accounts = await apiService.get<any[]>(
        `/bhph-accounts/?limit=5&buyer_name=${encodeURIComponent((deal.customer_first_name || '') + ' ' + (deal.customer_last_name || ''))}`
      );
      const match = accounts?.find((a: any) => a.deal_number === deal.deal_number);
      if (match) {
        navigate(`/bhph/account/${match.id}`);
      } else {
        toast.error('No BHPH account found. Close deal first.');
      }
    } catch {
      toast.error('Failed to find BHPH account');
    }
  };

  const generateContract = async () => {
    if (!id) return;
    try {
      await downloadDealDocuments(id);
      toast.success('Contract download started');
    } catch {
      toast.error('Could not download the contract. Make sure the deal has been finalized.');
    }
  };

  const printDealSheet = () => window.print();

  const saveNote = async () => {
    if (!noteModal.text.trim() || !deal) return;
    setNoteModal((m) => ({ ...m, submitting: true }));
    try {
      const combined = deal.notes
        ? `${deal.notes}\n\n${new Date().toLocaleString()}: ${noteModal.text.trim()}`
        : `${new Date().toLocaleString()}: ${noteModal.text.trim()}`;
      await apiService.put(`/deals/${id}`, { notes: combined });
      setDeal((d) => (d ? { ...d, notes: combined } : d));
      toast.success('Note saved');
      setNoteModal({ open: false, text: '', submitting: false });
    } catch {
      toast.error('Failed to save note');
      setNoteModal((m) => ({ ...m, submitting: false }));
    }
  };

  const markAsSold = async () => {
    if (!id || markingSold) return;
    setMarkingSold(true);
    try {
      await apiService.put(`/deals/${id}`, { deal_status: 'SOLD' });
      setDeal((d) => (d ? { ...d, deal_status: 'SOLD' } : d));
      toast.success('Deal marked as sold');
    } catch {
      toast.error('Failed to mark deal as sold');
    } finally {
      setMarkingSold(false);
    }
  };

  const handleSaveDeal = async (data: EditableDeal) => {
    if (!id) return;
    try {
      const updated = await apiService.put<Deal>(`/deals/${id}`, data);
      setDeal(updated);
      toast.success('Deal updated');
    } catch (err: unknown) {
      // PUT can 403 for salespeople editing a deal they don't own — surface the
      // real backend reason (thrown so the modal shows it and stays open).
      throw new Error(extractApiErrorMessage(err, 'Failed to update deal'));
    }
  };

  const getDealStatusBadge = (status: string) => {
    const colors = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      SOLD: 'bg-green-100 text-green-800',
      CANCELLED: 'bg-red-100 text-red-800',
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/deals')}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Deals
          </button>
        </div>
        <div className="card animate-pulse">
          <div className="h-8 bg-gray-300 rounded mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-300 rounded"></div>
            <div className="h-4 bg-gray-300 rounded"></div>
            <div className="h-4 bg-gray-300 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !deal) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/deals')}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Deals
          </button>
        </div>
        <div className="card">
          <div className="text-center py-12">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ReceiptRefundIcon className="h-6 w-6 text-red-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{error || 'Deal not found'}</h3>
            <p className="text-gray-500">
              The deal you're looking for doesn't exist or couldn't be loaded.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/deals')}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Deals
        </button>
        <button className="btn-primary" onClick={() => setEditOpen(true)}>
          <PencilIcon className="h-4 w-4 mr-2" />
          Edit Deal
        </button>
      </div>

      {/* Deal Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="card-header">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <ReceiptRefundIcon className="h-8 w-8 text-gray-400 mr-3" />
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">Deal #{deal.deal_number}</h1>
                    <div className="flex items-center space-x-2 mt-1">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getDealTypeBadge(deal.deal_type)}`}
                      >
                        {formatDealType(deal.deal_type)}
                      </span>
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getDealStatusBadge(deal.deal_status)}`}
                      >
                        {deal.deal_status}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Customer Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Customer Information</h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <UserCircleIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Customer</p>
                      <p className="text-sm text-gray-600">
                        {deal.customer_first_name} {deal.customer_last_name}
                      </p>
                    </div>
                  </div>
                  {deal.customer_phone && (
                    <div>
                      <p className="text-sm font-medium text-gray-900">Phone</p>
                      <p className="text-sm text-gray-600">{deal.customer_phone}</p>
                    </div>
                  )}
                  {deal.salesperson_name && (
                    <div>
                      <p className="text-sm font-medium text-gray-900">Salesperson</p>
                      <p className="text-sm text-gray-600">{deal.salesperson_name}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Vehicle Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Vehicle Information</h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <TruckIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Vehicle</p>
                      <p className="text-sm text-gray-600">
                        {deal.year} {deal.make} {deal.model}
                      </p>
                    </div>
                  </div>
                  {deal.vin && (
                    <div>
                      <p className="text-sm font-medium text-gray-900">VIN</p>
                      <p className="text-sm text-gray-600 font-mono">{deal.vin}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Pricing Information */}
            <div className="mt-6 pt-6 border-t">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Deal Summary</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-900">Purchase Price</p>
                  <p className="text-xl font-bold text-gray-900">
                    {formatCurrency(deal.purchase_price)}
                  </p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-900">Selling Price</p>
                  <p className="text-xl font-bold text-blue-900">
                    {formatCurrency(deal.selling_price)}
                  </p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-900">Down Payment</p>
                  <p className="text-xl font-bold text-green-900">
                    {formatCurrency(deal.down_payment_amount)}
                  </p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-900">Total Gross</p>
                  <p className="text-xl font-bold text-purple-900">
                    {formatCurrency(deal.total_gross)}
                  </p>
                </div>
              </div>
            </div>

            {/* Financing Information */}
            {isFinancedDeal(deal.deal_type) && (
              <div className="mt-6 pt-6 border-t">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Financing Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Financed Amount</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {formatCurrency(deal.amount_financed)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Monthly Payment</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {formatCurrency(deal.monthly_payment)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Term</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {deal.term_months || 0} months
                    </p>
                  </div>
                  {deal.interest_rate && (
                    <div>
                      <p className="text-sm font-medium text-gray-900">APR</p>
                      <p className="text-lg font-semibold text-gray-900">{deal.interest_rate}%</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* GPS Device Management - Only for BHPH deals. GPS is per-vehicle,
                so a deal with no linked vehicle shows a picker to attach one. */}
            {isBhphDeal(deal.deal_type) && (
              <div className="mt-6 pt-6 border-t">
                {deal.vehicle_id ? (
                  <GPSDeviceManager
                    dealId={deal.id}
                    dealType={deal.deal_type}
                    vehicleId={deal.vehicle_id}
                    customerId={deal.customer_id || ''}
                    onDeviceChange={() => {}}
                  />
                ) : (
                  <LinkVehicleCard dealId={deal.id} onLinked={fetchDeal} />
                )}
              </div>
            )}

            {/* Notes */}
            {deal.notes && (
              <div className="mt-6 pt-6 border-t">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Notes</h3>
                <p className="text-sm text-gray-600">{deal.notes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Activity Timeline */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">Activity Timeline</h3>
            </div>
            <div className="space-y-3">
              <div className="text-sm">
                <p className="font-medium text-gray-900">Deal Created</p>
                <p className="text-gray-600">{formatDate(deal.created_at)}</p>
              </div>
              {deal.updated_at && deal.updated_at !== deal.created_at && (
                <div className="text-sm">
                  <p className="font-medium text-gray-900">Last Updated</p>
                  <p className="text-gray-600">{formatDate(deal.updated_at)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">Quick Actions</h3>
            </div>
            <div className="space-y-2">
              {isBhphDeal(deal.deal_type) && deal.deal_status !== 'CLOSED' && (
                <>
                  <button
                    onClick={closeDealToBHPH}
                    disabled={closing || deal.deal_status === 'DRAFT'}
                    title={
                      deal.deal_status === 'DRAFT'
                        ? 'Finalize the deal before creating a BHPH account'
                        : undefined
                    }
                    className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg text-sm text-left transition-colors"
                  >
                    {closing ? 'Creating BHPH Account...' : 'Close Deal → Create BHPH Account'}
                  </button>
                  {deal.deal_status === 'DRAFT' && (
                    <p className="text-xs text-gray-500 mt-1">
                      Finalize the deal before creating a BHPH account.
                    </p>
                  )}
                  {closeError && <p className="text-xs text-red-600 mt-1">{closeError}</p>}
                </>
              )}
              {isBhphDeal(deal.deal_type) && (
                <>
                  <button onClick={viewPaymentSchedule} className="w-full btn-secondary text-left">
                    View Payment Schedule
                  </button>
                  <button onClick={recordPayment} className="w-full btn-secondary text-left">
                    Record Payment
                  </button>
                </>
              )}
              <button onClick={generateContract} className="w-full btn-secondary text-left">
                Generate Contract
              </button>
              <button onClick={printDealSheet} className="w-full btn-secondary text-left">
                Print Deal Sheet
              </button>
              <button
                onClick={() => setNoteModal({ open: true, text: '', submitting: false })}
                className="w-full btn-secondary text-left"
              >
                Add Note
              </button>
              {deal.deal_status === 'PENDING' && (
                <button
                  onClick={markAsSold}
                  disabled={markingSold}
                  className="w-full btn-secondary text-left disabled:opacity-50"
                >
                  {markingSold ? 'Marking...' : 'Mark as Sold'}
                </button>
              )}
            </div>
          </div>

          {/* Deal Documents */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">Documents</h3>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center">
                  <DocumentTextIcon className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-600">Sales Contract</span>
                </div>
                <span className="text-xs text-gray-400">Not Generated</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center">
                  <DocumentTextIcon className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-600">Title Documents</span>
                </div>
                <span className="text-xs text-gray-400">Not Uploaded</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center">
                  <DocumentTextIcon className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-600">Insurance Info</span>
                </div>
                <span className="text-xs text-gray-400">Not Available</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Schedule Modal */}
      {scheduleModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Payment Schedule</h3>
              <button
                onClick={() => setScheduleModal({ open: false, rows: [] })}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                &times;
              </button>
            </div>
            <div className="overflow-auto flex-1 p-4">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    {['#', 'Date', 'Payment', 'Principal', 'Interest', 'Balance'].map((h) => (
                      <th key={h} className="px-3 py-2 text-left font-medium text-gray-700">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {scheduleModal.rows.map((row: any, i: number) => (
                    <tr key={i} className="border-t hover:bg-gray-50">
                      <td className="px-3 py-2">{row.payment_number ?? i + 1}</td>
                      <td className="px-3 py-2">{row.payment_date ?? '—'}</td>
                      <td className="px-3 py-2">${(row.payment_amount ?? 0).toFixed(2)}</td>
                      <td className="px-3 py-2">${(row.principal_payment ?? 0).toFixed(2)}</td>
                      <td className="px-3 py-2">${(row.interest_payment ?? 0).toFixed(2)}</td>
                      <td className="px-3 py-2">${(row.remaining_balance ?? 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Add Note Modal */}
      {noteModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Note</h3>
            <textarea
              autoFocus
              rows={4}
              value={noteModal.text}
              onChange={(e) => setNoteModal((m) => ({ ...m, text: e.target.value }))}
              placeholder="Enter note..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
            />
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setNoteModal({ open: false, text: '', submitting: false })}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                disabled={!noteModal.text.trim() || noteModal.submitting}
                onClick={saveNote}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {noteModal.submitting ? 'Saving…' : 'Save Note'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Deal Modal */}
      <EditDealModal
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        onSave={handleSaveDeal}
        deal={deal}
      />
    </div>
  );
};

export default DealDetailPage;
